/**
 * sanitizeCandles — clamp spurious single-bar wick spikes ("bad ticks") in an OHLC
 * series so one corrupt print doesn't blow the chart's Y-scale (and any high/low-based
 * indicator) with a giant wick to the top/bottom of the pane.
 *
 * Conservative by design: a bar's high/low is clamped ONLY when it sticks out beyond its
 * immediate neighbours' extreme by more than OUTLIER_PCT of price (default 2%) — a move
 * real 5m/15m candles almost never make in a single wick, but a mis-scaled tick does.
 * The body (open/close) is never touched, so genuine directional bars are preserved.
 */
export interface OHLCRow { time: number; open: number; high: number; low: number; close: number; volume?: number; [k: string]: any }

const OUTLIER_PCT = 0.02; // wick must exceed the local reference by >2% of price to be a bad print

export function sanitizeCandles<T extends OHLCRow>(rows: T[]): T[] {
  const n = rows?.length || 0;
  if (n < 5) return rows;
  let changed = false;
  const out = rows.map((c, i) => {
    const o = Number(c.open), h = Number(c.high), l = Number(c.low), cl = Number(c.close);
    if (![o, h, l, cl].every(Number.isFinite) || h <= 0) return c;
    // Local window (excluding self) for a robust reference extreme.
    const lo = Math.max(0, i - 3), hi = Math.min(n - 1, i + 3);
    let refHigh = -Infinity, refLow = Infinity;
    for (let j = lo; j <= hi; j++) {
      if (j === i) continue;
      const jh = Number(rows[j].high), jl = Number(rows[j].low);
      if (Number.isFinite(jh)) refHigh = Math.max(refHigh, jh);
      if (Number.isFinite(jl)) refLow = Math.min(refLow, jl);
    }
    if (!Number.isFinite(refHigh) || !Number.isFinite(refLow)) return c;
    const bodyHi = Math.max(o, cl), bodyLo = Math.min(o, cl);
    const px = cl || bodyHi || h;
    const tol = px * OUTLIER_PCT;
    const capHi = Math.max(refHigh, bodyHi);   // plausible high = neighbours' high or this body
    const capLo = Math.min(refLow, bodyLo);
    let nh = h, nl = l;
    if (h > capHi + tol) nh = capHi;           // clamp spurious upper wick
    if (l < capLo - tol) nl = capLo;           // clamp spurious lower wick
    if (nh !== h || nl !== l) { changed = true; return { ...c, high: nh, low: nl }; }
    return c;
  });
  return changed ? out : rows;
}
